/**
 * EVENT TYPES
 * 
 * TypeScript interfaces for the Events Calendar feature.
 * Location: src/types/event.ts
 */

import { Timestamp } from 'firebase/firestore';

// ============================================
// EVENT TYPE ENUM
// ============================================

export type EventType = 
  | 'sale'           // Flash sales, clearance, etc.
  | 'restock'        // Returning items
  | 'release'        // New product launches
  | 'group_custom'   // Group custom orders
  | 'live_sale';     // Live shopping events (IG Live, FB Live, etc.)

export const EVENT_TYPE_CONFIG: Record<EventType, {
  label: string;
  emoji: string;
  color: string;      // Tailwind bg color
  textColor: string;  // Tailwind text color
  description: string;
}> = {
  sale: {
    label: 'Sale',
    emoji: '🏷️',
    color: 'bg-green-500',
    textColor: 'text-green-400',
    description: 'Flash sales, discounts, and clearance events'
  },
  restock: {
    label: 'Restock',
    emoji: '📦',
    color: 'bg-blue-500',
    textColor: 'text-blue-400',
    description: 'Previously sold-out items returning'
  },
  release: {
    label: 'New Release',
    emoji: '✨',
    color: 'bg-purple-500',
    textColor: 'text-purple-400',
    description: 'Brand new product launches'
  },
  group_custom: {
    label: 'Group Custom',
    emoji: '🎨',
    color: 'bg-pink-500',
    textColor: 'text-pink-400',
    description: 'Limited group custom orders'
  },
  live_sale: {
    label: 'Live Sale',
    emoji: '🔴',
    color: 'bg-red-500',
    textColor: 'text-red-400',
    description: 'Live shopping events on social media'
  }
};

// ============================================
// MAIN EVENT INTERFACE
// ============================================

export interface CalendarEvent {
  id?: string;
  
  // Core info
  title: string;
  description: string;
  eventType: EventType;
  
  // Timing
  startDate: Timestamp;
  endDate?: Timestamp;          // Optional for single-day events
  startTime?: string;           // "14:00" format, optional
  endTime?: string;             // "16:00" format, optional
  timezone?: string;            // e.g., "America/New_York"
  isAllDay: boolean;
  
  // Recurring event settings
  recurrence?: {
    pattern: 'none' | 'weekly' | 'monthly';
    weeklyDays?: number[];
    monthlyDay?: number;
    monthlyWeek?: 'first' | 'second' | 'third' | 'fourth' | 'last';
    monthlyWeekday?: number;
    endDate?: Timestamp;
  };
  parentEventId?: string;       // If this is a generated occurrence
  isRecurringInstance?: boolean;
  
  // Brand info (who created it)
  brandId: string;
  brandName: string;
  brandSlug: string;
  brandLogo?: string;
  
  // Links
  shopUrl?: string;             // Where to shop
  socialUrl?: string;           // IG Live link, FB event, etc.
  
  // Optional details
  featuredImage?: string;       // Event banner/promo image
  productsTeased?: string[];    // List of product names being released/restocked
  discountInfo?: string;        // "20% off everything" or "Free shipping"
  
  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;            // User ID who created it
  
  // Status
  isPublished: boolean;
  isFeatured?: boolean;         // Admin can feature events
}

// ============================================
// RECURRING EVENT TYPES
// ============================================

export type RecurrencePattern = 'none' | 'weekly' | 'monthly';

export interface RecurrenceConfig {
  pattern: RecurrencePattern;
  
  // For weekly: which day(s) of the week (0 = Sunday, 6 = Saturday)
  weeklyDays?: number[];
  
  // For monthly: which day of the month (1-31) or "first Saturday", etc.
  monthlyDay?: number;
  monthlyWeek?: 'first' | 'second' | 'third' | 'fourth' | 'last';
  monthlyWeekday?: number; // 0-6
  
  // Optional end date - if not set, recurs indefinitely
  endDate?: Date;
}

// ============================================
// REMINDER TYPES
// ============================================

export type ReminderTiming = 'atstart' | '5min' | '15min' | '30min' | '1hour' | '1day' | '1week';

export interface EventReminder {
  id?: string;
  eventId: string;
  userId: string;
  timing: ReminderTiming;
  eventTitle: string;
  eventDate: Date;
  brandName: string;
  reminderDate: Date;  // When to send the reminder
  sent: boolean;
  createdAt: Date;
}

export const REMINDER_OPTIONS: Record<ReminderTiming, { label: string; milliseconds: number }> = {
  'atstart': { label: 'When it starts', milliseconds: 0 },
  '5min': { label: '5 minutes before', milliseconds: 5 * 60 * 1000 },
  '15min': { label: '15 minutes before', milliseconds: 15 * 60 * 1000 },
  '30min': { label: '30 minutes before', milliseconds: 30 * 60 * 1000 },
  '1hour': { label: '1 hour before', milliseconds: 60 * 60 * 1000 },
  '1day': { label: '1 day before', milliseconds: 24 * 60 * 60 * 1000 },
  '1week': { label: '1 week before', milliseconds: 7 * 24 * 60 * 60 * 1000 }
};

// ============================================
// FOR CREATING/EDITING EVENTS
// ============================================

export interface EventFormData {
  title: string;
  description: string;
  eventType: EventType;
  
  startDate: Date;
  endDate?: Date;
  startTime?: string;
  endTime?: string;
  isAllDay: boolean;
  timezone?: string;  // Auto-captured from brand owner's browser
  
  shopUrl?: string;
  socialUrl?: string;
  
  featuredImage?: string;
  productsTeased?: string[];
  discountInfo?: string;
  
  // Recurring event settings
  recurrence?: RecurrenceConfig;
}

// ============================================
// FOR CALENDAR DISPLAY
// ============================================

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
}

// ============================================
// FILTERS
// ============================================

export interface EventFilters {
  eventTypes?: EventType[];
  brandId?: string;
  startAfter?: Date;
  startBefore?: Date;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get display info for an event type
 */
export function getEventTypeInfo(type: EventType) {
  return EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG.release;
}

/**
 * Format event date for display
 */
export function formatEventDate(event: CalendarEvent): string {
  const start = event.startDate.toDate();
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  };
  
  let dateStr = start.toLocaleDateString('en-US', options);
  
  if (event.endDate) {
    const end = event.endDate.toDate();
    if (start.toDateString() !== end.toDateString()) {
      dateStr += ` - ${end.toLocaleDateString('en-US', options)}`;
    }
  }
  
  return dateStr;
}

/**
 * Format event time for display
 */
export function formatEventTime(event: CalendarEvent): string | null {
  if (event.isAllDay) return 'All Day';
  if (!event.startTime) return null;
  
  // Convert 24h to 12h format
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  
  let timeStr = formatTime(event.startTime);
  
  if (event.endTime) {
    timeStr += ` - ${formatTime(event.endTime)}`;
  }
  
  if (event.timezone) {
    // Show abbreviated timezone
    const tzAbbr = new Date().toLocaleString('en-US', {
      timeZone: event.timezone,
      timeZoneName: 'short'
    }).split(' ').pop();
    timeStr += ` ${tzAbbr}`;
  }
  
  return timeStr;
}

/**
 * Check if an event is happening now
 */
export function isEventLive(event: CalendarEvent): boolean {
  const now = new Date();
  const start = event.startDate.toDate();
  const end = event.endDate?.toDate() || start;
  
  // For all-day events, check if today is within the range
  if (event.isAllDay) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return today >= startDay && today <= endDay;
  }
  
  // For timed events, check exact times
  return now >= start && now <= end;
}

/**
 * Check if an event is upcoming (within next 7 days)
 */
export function isEventUpcoming(event: CalendarEvent): boolean {
  const now = new Date();
  const start = event.startDate.toDate();
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  return start > now && start <= weekFromNow;
}

/**
 * Get user's current timezone
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format date for ICS file (with timezone)
 */
function formatICSDateWithTimezone(date: Date, time: string | undefined, _timezone: string): string {
  if (!time) {
    // All-day event - use DATE format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }
  
  // Event with specific time
  const [hours, minutes] = time.split(':');
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}00`;
}

/**
 * Generate ICS file content for calendar export
 */
export function generateICSContent(event: CalendarEvent): string {
  const startDate = event.startDate.toDate();
  const endDate = event.endDate?.toDate() || startDate;
  const timezone = event.timezone || 'America/New_York'; // Default if not set
  
  const escapeICS = (str: string) => {
    return str.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  };
  
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Polish ALL the Things//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@polishallthethings.com`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
  ];
  
  if (event.isAllDay) {
    // All-day events use VALUE=DATE
    lines.push(`DTSTART;VALUE=DATE:${formatICSDateWithTimezone(startDate, undefined, timezone)}`);
    // End date for all-day is exclusive, so add 1 day
    const endPlusOne = new Date(endDate);
    endPlusOne.setDate(endPlusOne.getDate() + 1);
    lines.push(`DTEND;VALUE=DATE:${formatICSDateWithTimezone(endPlusOne, undefined, timezone)}`);
  } else {
    // Timed events include timezone
    lines.push(`DTSTART;TZID=${timezone}:${formatICSDateWithTimezone(startDate, event.startTime, timezone)}`);
    
    // Calculate end time
    let endTime = event.endTime;
    if (!endTime && event.startTime) {
      // Default to 1 hour after start
      const [h, m] = event.startTime.split(':').map(Number);
      endTime = `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    lines.push(`DTEND;TZID=${timezone}:${formatICSDateWithTimezone(endDate, endTime || event.startTime, timezone)}`);
  }
  
  lines.push(`SUMMARY:${escapeICS(event.title)} - ${escapeICS(event.brandName)}`);
  
  let description = event.description || '';
  if (event.shopUrl) description += `\\n\\nShop: ${event.shopUrl}`;
  if (event.discountInfo) description += `\\n\\nDeal: ${event.discountInfo}`;
  if (description) lines.push(`DESCRIPTION:${escapeICS(description)}`);
  
  if (event.shopUrl) lines.push(`URL:${event.shopUrl}`);
  
  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');
  
  return lines.join('\r\n');
}

/**
 * Download ICS file for an event
 */
export function downloadEventICS(event: CalendarEvent): void {
  const icsContent = generateICSContent(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${event.title.replace(/[^a-z0-9]/gi, '-')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generate Google Calendar URL for an event
 */
export function generateGoogleCalendarUrl(event: CalendarEvent): string {
  const startDate = event.startDate.toDate();
  const endDate = event.endDate?.toDate() || startDate;
  const timezone = event.timezone || 'America/New_York';
  
  // Format dates for Google Calendar
  const formatGoogleDate = (date: Date, time: string | undefined, isAllDay: boolean): string => {
    if (isAllDay) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const [hours, minutes] = (time || '12:00').split(':');
    return `${year}${month}${day}T${hours}${minutes}00`;
  };
  
  const startStr = formatGoogleDate(startDate, event.startTime, event.isAllDay);
  
  let endStr: string;
  if (event.isAllDay) {
    // End date for all-day is exclusive
    const endPlusOne = new Date(endDate);
    endPlusOne.setDate(endPlusOne.getDate() + 1);
    endStr = formatGoogleDate(endPlusOne, undefined, true);
  } else {
    let endTime = event.endTime;
    if (!endTime && event.startTime) {
      const [h, m] = event.startTime.split(':').map(Number);
      endTime = `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    endStr = formatGoogleDate(endDate, endTime || event.startTime, false);
  }
  
  // Build description
  let description = event.description || '';
  if (event.shopUrl) description += `\n\nShop: ${event.shopUrl}`;
  if (event.discountInfo) description += `\n\nDeal: ${event.discountInfo}`;
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${event.title} - ${event.brandName}`,
    dates: `${startStr}/${endStr}`,
    details: description,
    ctz: timezone
  });
  
  if (event.shopUrl) {
    params.set('sprop', `website:${event.shopUrl}`);
  }
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate next occurrence dates for a recurring event
 */
export function getNextOccurrences(
  event: CalendarEvent,
  fromDate: Date,
  count: number = 12
): Date[] {
  if (!event.recurrence || event.recurrence.pattern === 'none') {
    return [event.startDate.toDate()];
  }
  
  const occurrences: Date[] = [];
  const endDate = event.recurrence.endDate?.toDate();
  let currentDate = new Date(fromDate);
  
  // Start from the event's original start date if it's in the future
  const originalStart = event.startDate.toDate();
  if (originalStart > currentDate) {
    currentDate = new Date(originalStart);
  }
  
  while (occurrences.length < count) {
    // Check if we've passed the end date
    if (endDate && currentDate > endDate) break;
    
    if (event.recurrence.pattern === 'weekly') {
      const weeklyDays = event.recurrence.weeklyDays || [currentDate.getDay()];
      
      for (const day of weeklyDays) {
        const nextOccurrence = new Date(currentDate);
        const daysUntil = (day - currentDate.getDay() + 7) % 7;
        nextOccurrence.setDate(currentDate.getDate() + daysUntil);
        
        if (nextOccurrence >= fromDate && (!endDate || nextOccurrence <= endDate)) {
          occurrences.push(new Date(nextOccurrence));
          if (occurrences.length >= count) break;
        }
      }
      
      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7);
    } else if (event.recurrence.pattern === 'monthly') {
      if (event.recurrence.monthlyDay) {
        // Specific day of month (e.g., 15th)
        const nextOccurrence = new Date(currentDate.getFullYear(), currentDate.getMonth(), event.recurrence.monthlyDay);
        if (nextOccurrence < currentDate) {
          nextOccurrence.setMonth(nextOccurrence.getMonth() + 1);
        }
        
        if (nextOccurrence >= fromDate && (!endDate || nextOccurrence <= endDate)) {
          occurrences.push(new Date(nextOccurrence));
        }
      } else if (event.recurrence.monthlyWeek && event.recurrence.monthlyWeekday !== undefined) {
        // Nth weekday of month (e.g., "first Saturday")
        const nextOccurrence = getNthWeekdayOfMonth(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          event.recurrence.monthlyWeekday,
          event.recurrence.monthlyWeek
        );
        
        if (nextOccurrence && nextOccurrence < currentDate) {
          const nextMonth = getNthWeekdayOfMonth(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            event.recurrence.monthlyWeekday,
            event.recurrence.monthlyWeek
          );
          if (nextMonth && nextMonth >= fromDate && (!endDate || nextMonth <= endDate)) {
            occurrences.push(nextMonth);
          }
        } else if (nextOccurrence && nextOccurrence >= fromDate && (!endDate || nextOccurrence <= endDate)) {
          occurrences.push(nextOccurrence);
        }
      }
      
      // Move to next month
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  }
  
  return occurrences.sort((a, b) => a.getTime() - b.getTime());
}

/**
 * Get the Nth weekday of a month
 */
function getNthWeekdayOfMonth(
  year: number,
  month: number,
  weekday: number, // 0 = Sunday, 6 = Saturday
  which: 'first' | 'second' | 'third' | 'fourth' | 'last'
): Date | null {
  if (which === 'last') {
    // Start from last day of month and work backwards
    const lastDay = new Date(year, month + 1, 0);
    for (let d = lastDay.getDate(); d >= 1; d--) {
      const date = new Date(year, month, d);
      if (date.getDay() === weekday) return date;
    }
    return null;
  }
  
  const weekNum = { first: 1, second: 2, third: 3, fourth: 4 }[which];
  let count = 0;
  
  for (let d = 1; d <= 31; d++) {
    const date = new Date(year, month, d);
    if (date.getMonth() !== month) break; // Went to next month
    
    if (date.getDay() === weekday) {
      count++;
      if (count === weekNum) return date;
    }
  }
  
  return null;
}
